import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CustomPagination from "../util/CustomPagination";
import DropdownPortal from "../util/DropdownPortal";
import VideoClientDetailModal from "./VideoClientDetailModal";
import VideoClientEditModal from "./VideoClientEditModal";

require("./assets/css/Video.css");

const VideoClientsPage = () => {
    document.title = "Clientes de Vídeo | Plantão Rápido";

    const buttonRefs = useRef({});
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [search, setSearch] = useState("");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0,
        lastPage: 0
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async (page = 1) => {
        try {
            setLoading(true);

            const params = {
                page: page,
                per_page: pagination.pageSize
            };

            if (search) {
                params.search = search;
            }

            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/clients`,
                params,
                true
            );

            const { data, pagination: pagInfo } = ApiService.handleApiResponse(response);
            setClients(data || []);
            setPagination(pagInfo || { current: 1, pageSize: 15, total: 0 });
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        fetchClients(page);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchClients(1);
    };

    const handleViewDetail = async (client) => {
        try {
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/clients/${client.id}/attendances`,
                {},
                true
            );

            const clientDetail = {
                ...client,
                attendances: response.data || []
            };

            setSelectedClient(clientDetail);
            setShowDetailModal(true);
        } catch (error) {
            setSelectedClient(client);
            setShowDetailModal(true);
        }
    };

    const handleEdit = (client) => {
        setSelectedClient(client);
        setShowEditModal(true);
    };

    const handleCreate = () => {
        setSelectedClient(null);
        setShowEditModal(true);
    };

    const handleDelete = async (client) => {
        const result = await Swal.fire({
            title: 'Excluir cliente?',
            text: `Tem certeza que deseja excluir "${client.name}"? Esta ação não pode ser desfeita.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, excluir',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await ApiService.send(
                "delete",
                `${process.env.REACT_APP_URL_API}video/clients/${client.id}`,
                {},
                true
            );

            if (response.code === 200) {
                Swal.fire('Excluído', 'Cliente excluído com sucesso.', 'success');
                fetchClients(pagination.current);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Clientes" pageTitle="Vídeo Chamada" />

                    {/* Stats Cards */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <div className="video-stats-card">
                                <div className="stat-value">{pagination.total}</div>
                                <div className="stat-label">Total de Clientes</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Search and Actions */}
                    <Row className="mb-3">
                        <Col md={6}>
                            <form onSubmit={handleSearch}>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar por nome, email ou telefone..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <button
                                        className="btn btn-success"
                                        type="submit"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                            <i className="fa fa-search"></i>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </Col>
                        <Col md={6} className="text-end">
                            <button
                                className="btn btn-outline-primary me-2"
                                onClick={() => fetchClients(pagination.current)}
                                disabled={loading}
                            >
                                <i className="fa fa-refresh me-2"></i> Atualizar
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreate}
                            >
                                <i className="fa fa-plus me-2"></i> Novo Cliente
                            </button>
                        </Col>
                    </Row>

                    {/* Clients List */}
                    <div className="list-container">
                        {loading ? (
                            <Skeleton count={5} height={80} />
                        ) : clients.length > 0 ? (
                            clients.map((client) => (
                                <Col lg={12} key={client.id}>
                                    <Card className="mb-2 p-2 shadow-sm">
                                        <CardBody className="d-flex justify-content-between align-items-center p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="video-user-avatar me-3">
                                                    {client.name?.charAt(0)?.toUpperCase() || 'C'}
                                                </div>
                                                <div>
                                                    <h5 className="mb-1">{client.name || 'Cliente'}</h5>
                                                    <p className="text-muted mb-1">
                                                        {client.email && (
                                                            <span>
                                                                <i className="fa fa-envelope me-1"></i>
                                                                {client.email}
                                                            </span>
                                                        )}
                                                        {client.email && client.phone && <span className="mx-2">|</span>}
                                                        {client.phone && (
                                                            <span>
                                                                <i className="fa fa-phone me-1"></i>
                                                                {client.phone}
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-muted mb-0 small">
                                                        <i className="fa fa-calendar me-1"></i>
                                                        Cadastrado em: {formatDate(client.created_at)}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="text-center">
                                                <span className="badge bg-info">
                                                    <i className="fa fa-video-camera me-1"></i>
                                                    {client.attendances_count || 0} atendimentos
                                                </span>
                                            </div>

                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    ref={(el) => (buttonRefs.current[client.id] = el)}
                                                    onClick={() => setDropdownOpenId(dropdownOpenId === client.id ? null : client.id)}
                                                >
                                                    <i className="fa fa-ellipsis-v"></i> Ações
                                                </button>

                                                <DropdownPortal
                                                    parentRef={{ current: buttonRefs.current[client.id] }}
                                                    show={dropdownOpenId === client.id}
                                                    onClose={() => setDropdownOpenId(null)}
                                                >
                                                    <ul className="dropdown-menu show">
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    handleViewDetail(client);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-eye me-2"></i> Ver Detalhes
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    handleEdit(client);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-edit me-2"></i> Editar
                                                            </button>
                                                        </li>
                                                        <li><hr className="dropdown-divider" /></li>
                                                        <li>
                                                            <button
                                                                className="dropdown-item text-danger"
                                                                onClick={() => {
                                                                    handleDelete(client);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-trash me-2"></i> Excluir
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </DropdownPortal>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <div className="video-empty-state">
                                <i className="fa fa-users"></i>
                                <h5 className="text-muted">Nenhum cliente encontrado</h5>
                                <p className="text-muted">Os clientes que solicitarem atendimento por vídeo aparecerão aqui.</p>
                                <button className="btn btn-primary mt-3" onClick={handleCreate}>
                                    <i className="fa fa-plus me-2"></i> Adicionar Cliente
                                </button>
                            </div>
                        )}
                    </div>

                    <CustomPagination
                        onChange={handlePageChange}
                        pagination={pagination}
                    />

                    {showDetailModal && (
                        <VideoClientDetailModal
                            show={showDetailModal}
                            onHide={() => {
                                setShowDetailModal(false);
                                setSelectedClient(null);
                            }}
                            client={selectedClient}
                        />
                    )}

                    {showEditModal && (
                        <VideoClientEditModal
                            show={showEditModal}
                            onHide={() => {
                                setShowEditModal(false);
                                setSelectedClient(null);
                            }}
                            client={selectedClient}
                            onSave={() => fetchClients(pagination.current)}
                        />
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VideoClientsPage;
